import { FC } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { useCategoryWithValues } from '../../api/tenant-profile-template-api';
import type { ActiveTemplateCategoryMeta } from '../../api/types';
import { categoryIcon } from '../utils/categoryIcon';
import type { DerivedCategory } from '../utils/deriveCategoryStatus';

import { CategoryEditForm } from './CategoryEditForm';

interface Props {
  meta: ActiveTemplateCategoryMeta;
  derived: DerivedCategory;
}

/**
 * The selected category's panel: a header (icon + name + fields-filled count +
 * description) rendered immediately from the outline meta, followed by the
 * field form whose fields are lazy-loaded for the active category.
 */
export const CategoryPanel: FC<Props> = observer(
  ({ meta, derived }) => {
    const { t } = useTrans();
    const categoryQuery = useCategoryWithValues(meta.id);
    const category = categoryQuery.data;

    const name = meta.metadata.name || meta.key;
    const description = meta.metadata.description;
    const IconCmp = categoryIcon(meta.key, name);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.25,
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                backgroundColor: 'grey.200',
                border: 1,
                borderColor: 'grey.300',
              }}
            >
              <IconCmp size={18} />
            </Box>
            <Typography variant="textLG_bold" color="text.primary">
              {name}
            </Typography>
            <Box
              component="span"
              sx={{
                typography: 'textXS_semiBold',
                px: 1,
                py: '3px',
                borderRadius: 999,
                backgroundColor: 'grey.200',
                color: 'text.tertiary',
              }}
            >
              <Box component="span" sx={{ typography: 'textXS_bold', color: 'green.700' }}>
                {derived.filledCount}
              </Box>{' '}
              {t('app.orgProfile.panel.fieldsFilledSuffix', { total: derived.totalFields })}
            </Box>
          </Box>
          <Typography variant="textSM_regular" color="text.tertiary">
            {description ? `${description} · ` : ''}
            <Box component="span" sx={{ typography: 'textSM_bold', color: 'text.secondary' }}>
              {t('app.orgProfile.panel.fieldCount', { n: derived.totalFields })}
            </Box>{' '}
            {t('app.orgProfile.panel.fieldCountSuffix', { category: name })}
          </Typography>
        </Box>

        <Box sx={{ height: '1px', backgroundColor: 'grey.300' }} />

        {categoryQuery.isError ? (
          <Typography variant="textSM_regular" color="yellow.800">
            {t('app.orgProfile.loadError')}
          </Typography>
        ) : !category ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <CategoryEditForm
            key={category.id}
            category={category}
            categoryName={name}
          />
        )}
      </Box>
    );
  },
);
